import { inject, Injectable } from '@angular/core';
import { EncryptionService } from '@evorait/pace-encryption';
import { LogInstanceToken } from '@evorait/pace-logging';
import Dexie, { Table } from 'dexie';
import { from, Observable, of } from 'rxjs';
import { catchError, filter, map, switchMap, take } from 'rxjs/operators';
import { UserMasterState } from '../../state/user-master';

/**
 * A single stored record: the selected user, encrypted at rest. Keyed by a constant id
 * because only one user is ever selected at a time.
 */
interface SelectedUserRecord {
    id: string;
    data: string;
}

/**
 * Encrypted IndexedDB persistence for the selected user-master user, replacing the legacy
 * encrypted-`localStorage` `SalesAndPlant` entry for the NgRx path. Values are encrypted via
 * {@link EncryptionService} (mirroring `LocalStorageCommonService`) so the PII in
 * {@link JsonUserModel} (`emailId`, `personnelArea`, `frontId`) stays encrypted at rest.
 */
@Injectable({ providedIn: 'root' })
export class StorageUserMasterService extends Dexie {
    private readonly encryption = inject(EncryptionService);
    private readonly log = inject(LogInstanceToken);

    public static readonly DB_NAME = 'UserMasterDB';
    public static readonly DB_TABLE = 'selectedUser';
    private static readonly RECORD_ID = 'current';
    private static readonly VERSION_ONE = 1;

    private selectedUser: Table<SelectedUserRecord, string>;

    public constructor() {
        super(StorageUserMasterService.DB_NAME);
        this.version(StorageUserMasterService.VERSION_ONE).stores({
            [StorageUserMasterService.DB_TABLE]: 'id',
        });
        this.selectedUser = this.table<SelectedUserRecord, string>(StorageUserMasterService.DB_TABLE);
    }

    /**
     * Read the persisted selected user, or `undefined` when none is stored (or on error).
     */
    public getSelectedUser$(): Observable<UserMasterState | undefined> {
        return this.waitForEncryption().pipe(
            switchMap(() => from(this.selectedUser.get(StorageUserMasterService.RECORD_ID))),
            switchMap(async (record) =>
                record ? (JSON.parse(await this.encryption.decryptAsync(record.data)) as UserMasterState) : undefined,
            ),
            catchError((error) => {
                this.log.error(`${this.constructor.name}: getSelectedUser$: failed to read selected user`, error);
                return of(undefined);
            }),
        );
    }

    /**
     * Persist the selected user (encrypted). Emits the stored user on success.
     *
     * @param user
     */
    public saveSelectedUser$(user: UserMasterState): Observable<UserMasterState> {
        return this.waitForEncryption().pipe(
            switchMap(async () => {
                const data = await this.encryption.encryptAsync(JSON.stringify(user));
                await this.selectedUser.put({ id: StorageUserMasterService.RECORD_ID, data });
                return user;
            }),
            catchError((error) => {
                this.log.error(`${this.constructor.name}: saveSelectedUser$: failed to persist selected user`, error);
                return of(user);
            }),
        );
    }

    /**
     * Remove the persisted selected user.
     */
    public clear$(): Observable<void> {
        return from(this.selectedUser.delete(StorageUserMasterService.RECORD_ID)).pipe(
            map(() => undefined),
            catchError((error) => {
                this.log.error(`${this.constructor.name}: clear$: failed to clear selected user`, error);
                return of(undefined);
            }),
        );
    }

    private waitForEncryption(): Observable<boolean> {
        return this.encryption.selectInitialized.pipe(filter(Boolean), take(1));
    }
}
